import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Copy, Check, QrCode, Download, Code, Sparkles } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export const WidgetSettings = ({ hotelId, hotelName }) => {
  const [widgetColor, setWidgetColor] = useState('#2563eb');
  const [buttonText, setButtonText] = useState('Book Now');
  const [copied, setCopied] = useState('');
  const checkinQRRef = useRef();
  const walkinQRRef = useRef();

  const appUrl = BACKEND_URL?.replace('/api', '') || window.location.origin;
  const checkinUrl = `${appUrl}/checkin?hotel=${hotelId}`;
  const walkinUrl = `${appUrl}/walk-in?hotel=${hotelId}`;

  const embedCode = `<!-- getbitsy.ai Booking Widget -->
<script 
  src="${appUrl}/embed-widget.js"
  data-hotel-id="${hotelId}"
  data-color="${widgetColor}"
  data-button-text="${buttonText}"
  data-position="bottom-right"
></script>`;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  const downloadQR = (ref, filename) => {
    const canvas = ref.current.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.png`;
      a.click();
      toast.success('QR code downloaded!');
    }
  };

  return (
    <div className="space-y-6" data-testid="widget-settings">
      <div>
        <h3 className="text-lg font-semibold heading-font mb-1">Embeddable Widget & QR Codes</h3>
        <p className="text-sm text-muted-foreground">
          Add booking functionality to your website and enable self-service check-in
        </p>
      </div>

      <Tabs defaultValue="widget" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="widget" data-testid="tab-widget">
            <Code className="w-4 h-4 mr-2" />
            Widget
          </TabsTrigger>
          <TabsTrigger value="checkin-qr" data-testid="tab-checkin-qr">
            <QrCode className="w-4 h-4 mr-2" />
            Check-in QR
          </TabsTrigger>
          <TabsTrigger value="walkin-qr" data-testid="tab-walkin-qr">
            <Sparkles className="w-4 h-4 mr-2" />
            Walk-in QR
          </TabsTrigger>
        </TabsList>

        {/* Embeddable Widget Tab */}
        <TabsContent value="widget" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Customize Widget</CardTitle>
              <CardDescription>Adjust colors and text to match your brand</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Button Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={widgetColor}
                      onChange={(e) => setWidgetColor(e.target.value)}
                      className="w-20 h-10"
                      data-testid="widget-color-picker"
                    />
                    <Input
                      value={widgetColor}
                      onChange={(e) => setWidgetColor(e.target.value)}
                      placeholder="#2563eb"
                      className="flex-1 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label>Button Text</Label>
                  <Input
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    placeholder="Book Now"
                    data-testid="widget-button-text"
                  />
                </div>
              </div>

              {/* Widget Preview */}
              <div className="bg-muted rounded-lg p-8 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground mb-4">Preview</p>
                  <button
                    style={{ background: widgetColor }}
                    className="flex items-center gap-2 px-5 py-3 text-white rounded-full font-semibold shadow-lg hover:scale-105 transition-transform"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <span className="hidden sm:inline">{buttonText}</span>
                  </button>
                  <p className="text-xs text-muted-foreground mt-4">
                    Desktop: Full pill • Mobile: Compact
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Embed Code</CardTitle>
              <CardDescription>Copy this code and paste it before &lt;/body&gt; tag on your website</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-slate-900 text-slate-50 rounded-lg p-4 text-xs overflow-x-auto font-mono">
                  <code>{embedCode}</code>
                </pre>
                <Button
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(embedCode, 'Embed code')}
                  data-testid="copy-embed-code"
                >
                  {copied === 'Embed code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>

              <Alert className="mt-4">
                <AlertDescription className="text-xs">
                  <strong>Note:</strong> The widget opens a large modal (80% screen on desktop, full-screen on mobile) with your hotel's AI booking chat. Guests can view photos, check availability, and complete bookings directly on your site.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Check-in QR Tab */}
        <TabsContent value="checkin-qr" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Self Check-In QR Code</CardTitle>
              <CardDescription>
                For guests with existing getbitsy.ai bookings. Display this at your front desk or lobby.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                <div ref={checkinQRRef} className="bg-white p-6 rounded-xl shadow-sm" data-testid="checkin-qr-code">
                  <QRCodeCanvas
                    value={checkinUrl}
                    size={220}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                
                <div className="text-center">
                  <p className="text-sm font-medium mb-1">Check-In at {hotelName}</p>
                  <p className="text-xs text-muted-foreground font-mono">{checkinUrl}</p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => downloadQR(checkinQRRef, `${hotelId}-checkin-qr`)}
                    data-testid="download-checkin-qr"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(checkinUrl, 'Check-in URL')}
                  >
                    {copied === 'Check-in URL' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied === 'Check-in URL' ? 'Copied!' : 'Copy URL'}
                  </Button>
                </div>
              </div>

              <Alert>
                <AlertDescription className="text-xs">
                  <strong>How it works:</strong> Guests scan this QR code → Enter email or confirmation code → System finds their booking → They tap "Check In" → You see them as checked in on your dashboard.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Walk-in QR Tab */}
        <TabsContent value="walkin-qr" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Walk-In Booking QR Code</CardTitle>
              <CardDescription>
                For guests without reservations. They can book and check-in instantly using their phone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                <div ref={walkinQRRef} className="bg-white p-6 rounded-xl shadow-sm" data-testid="walkin-qr-code">
                  <QRCodeCanvas
                    value={walkinUrl}
                    size={220}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                
                <div className="text-center">
                  <p className="text-sm font-medium mb-1">Book Now at {hotelName}</p>
                  <p className="text-xs text-muted-foreground font-mono">{walkinUrl}</p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => downloadQR(walkinQRRef, `${hotelId}-walkin-qr`)}
                    data-testid="download-walkin-qr"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(walkinUrl, 'Walk-in URL')}
                  >
                    {copied === 'Walk-in URL' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied === 'Walk-in URL' ? 'Copied!' : 'Copy URL'}
                  </Button>
                </div>
              </div>

              <Alert>
                <AlertDescription className="text-xs">
                  <strong>How it works:</strong> Walk-in guests scan → Fill info (name, email, dates, guests) → View available rooms with photos → Select room → Choose payment → Booking created & auto checked-in! Perfect for front desk display.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WidgetSettings;
